import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadFile, apiError } from "@/lib/api";
import { formatEUR, formatDate, STATUS_OPTIONS } from "@/lib/invoice";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Pencil, Trash2, FileDown, FileText, Loader2, Wallet, CircleDollarSign,
  AlertTriangle, Files, Search, Copy,
} from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, sub, tone = "slate", testid }) => {
  const tones = {
    slate: "text-slate-900",
    blue: "text-primary",
    green: "text-green-700",
    red: "text-red-600",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide mb-2">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className={`font-mono text-2xl font-semibold ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusValue, setStatusValue] = useState("offen");
  const [paidAmount, setPaidAmount] = useState(0);

  const load = async () => {
    try {
      const [inv, st] = await Promise.all([api.get("/invoices"), api.get("/invoices/stats")]);
      setInvoices(inv.data);
      setStats(st.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/invoices/${delTarget.id}`);
      toast.success("Rechnung gelöscht");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openStatus = (inv) => {
    setStatusTarget(inv);
    setStatusValue(inv.payment_status);
    setPaidAmount(inv.paid_amount || 0);
  };

  const saveStatus = async () => {
    try {
      await api.patch(`/invoices/${statusTarget.id}/status`, {
        payment_status: statusValue,
        paid_amount: statusValue === "teilbezahlt" ? Number(paidAmount) || 0 : 0,
      });
      toast.success("Status aktualisiert");
      setStatusTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDownload = async (inv, type) => {
    try {
      await downloadFile(
        `/invoices/${inv.id}/${type}`,
        `Rechnung_${inv.invoice_number}.${type}`
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = invoices.filter((i) => {
    const t = `${i.invoice_number} ${i.recipient?.name || ""} ${i.recipient?.company || ""}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Rechnungen</h1>
          <p className="text-slate-500 text-sm mt-1">Übersicht, Verwaltung & Zahlungsverfolgung</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Files} label="Rechnungen" value={stats.total} sub={`Gesamt ${formatEUR(stats.sum_total)}`} testid="stat-total" />
          <StatCard icon={Wallet} label="Offen" value={formatEUR(stats.sum_open)} sub={`${stats.offen + stats.teilbezahlt + stats.ueberfaellig} Rechnung(en)`} tone="blue" testid="stat-open" />
          <StatCard icon={CircleDollarSign} label="Bezahlt" value={formatEUR(stats.sum_paid)} sub={`${stats.bezahlt} bezahlt`} tone="green" testid="stat-paid" />
          <StatCard icon={AlertTriangle} label="Überfällig" value={stats.ueberfaellig} sub="Fälligkeit überschritten" tone="red" testid="stat-overdue" />
        </div>
      )}

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="invoice-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche Nr. oder Empfänger…"
              className="rounded-none border-slate-300 pl-8 focus-visible:ring-0 focus-visible:border-primary"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">Noch keine Rechnungen vorhanden.</p>
            <Button
              data-testid="empty-new-invoice"
              onClick={() => navigate("/editor")}
              className="rounded-none bg-primary hover:bg-[#00227A]"
            >
              Erste Rechnung erstellen
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-mono text-xs">Nr.</TableHead>
                <TableHead>Empfänger</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="group">
                  <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-800">
                      {inv.recipient?.company || inv.recipient?.name || "—"}
                    </div>
                    {inv.recipient?.company && inv.recipient?.name && (
                      <div className="text-xs text-slate-400">{inv.recipient.name}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDate(inv.invoice_date)}</TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {formatEUR(inv.totals?.gross_total)}
                  </TableCell>
                  <TableCell>
                    <button data-testid={`status-toggle-${inv.id}`} onClick={() => openStatus(inv)}>
                      <StatusBadge status={inv.payment_status} className="cursor-pointer hover:opacity-80" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <IconBtn testid={`edit-${inv.id}`} title="Bearbeiten" onClick={() => navigate(`/editor/${inv.id}`)}>
                        <Pencil className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn testid={`duplicate-${inv.id}`} title="Duplizieren" onClick={() => navigate(`/editor?duplicate=${inv.id}`)}>
                        <Copy className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn testid={`pdf-${inv.id}`} title="PDF" onClick={() => doDownload(inv, "pdf")}>
                        <FileDown className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn testid={`docx-${inv.id}`} title="Word" onClick={() => doDownload(inv, "docx")}>
                        <FileText className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn testid={`delete-${inv.id}`} title="Löschen" danger onClick={() => setDelTarget(inv)}>
                        <Trash2 className="h-4 w-4" />
                      </IconBtn>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Delete dialog */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Rechnung löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Rechnung {delTarget?.invoice_number} wird unwiderruflich gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete"
              onClick={confirmDelete}
              className="rounded-none bg-destructive hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status dialog */}
      <Dialog open={!!statusTarget} onOpenChange={(o) => !o && setStatusTarget(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Zahlungsstatus – {statusTarget?.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={statusValue} onValueChange={setStatusValue}>
                <SelectTrigger data-testid="status-select" className="rounded-none border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {statusValue === "teilbezahlt" && (
              <div className="space-y-1.5">
                <Label>Bereits gezahlt (€)</Label>
                <Input
                  type="number"
                  data-testid="paid-amount"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  className="rounded-none border-slate-300 font-mono focus-visible:ring-0 focus-visible:border-primary"
                />
                <p className="text-xs text-slate-400">
                  Gesamtbetrag: {formatEUR(statusTarget?.totals?.gross_total)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setStatusTarget(null)}>
              Abbrechen
            </Button>
            <Button
              data-testid="save-status"
              onClick={saveStatus}
              className="rounded-none bg-primary hover:bg-[#00227A]"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const IconBtn = ({ children, onClick, title, danger, testid }) => (
  <button
    data-testid={testid}
    title={title}
    onClick={onClick}
    className={`h-8 w-8 flex items-center justify-center border border-transparent transition-colors ${
      danger
        ? "text-slate-400 hover:text-destructive hover:border-red-200 hover:bg-red-50"
        : "text-slate-500 hover:text-primary hover:border-blue-200 hover:bg-blue-50"
    }`}
  >
    {children}
  </button>
);
